/**
 * Extract a human-readable error message from an API error.
 * Handles Axios errors (error.response.data.error), standard Error objects,
 * and arbitrary throwables.
 */

import type { AxiosLikeError } from '@/shared/types/httpError.types';

function isAxiosLikeError(error: unknown): error is AxiosLikeError {
  return error != null && typeof error === 'object' && ('response' in error || 'message' in error);
}

function responseDataErrorMessage(data: unknown): string | undefined {
  if (data != null && typeof data === 'object' && 'error' in data && typeof (data as { error: unknown }).error === 'string') {
    return (data as { error: string }).error;
  }
  return undefined;
}

export function getApiErrorMessage(error: unknown, fallback: string = 'An unexpected error occurred'): string {
  if (isAxiosLikeError(error)) {
    const fromBody = responseDataErrorMessage(error.response?.data);
    if (fromBody) return fromBody;
    if (error.message) return error.message;
  }
  if (typeof error === 'string') return error;
  return fallback;
}

/** Extract detailed error info for logging (response data, status, stack). */
export function getApiErrorDetails(error: unknown): { message: string; responseData?: unknown; status?: number; stack?: string } {
  if (isAxiosLikeError(error)) {
    return {
      message: error.message || String(error),
      responseData: error.response?.data,
      status: error.response?.status,
      stack: error.stack,
    };
  }
  return { message: String(error) };
}
